const fontSizeCookieKey = 'html-font-size'

export const FONT_SIZE_MIN = 12
export const FONT_SIZE_MAX = 22
const FONT_SIZE_DEFAULT = 16

const clamp = (value: number) =>
  Math.min(FONT_SIZE_MAX, Math.max(FONT_SIZE_MIN, Math.round(value)))

/**
 * Admin-only root font-size control. Persists to the `html-font-size` cookie so
 * the chosen size survives reloads and is available during SSR. Applying the
 * value to the <html> element is the caller's job (see app.vue useHead).
 */
export const useFontSize = () => {
  const cookie = useCookie<number>(fontSizeCookieKey, {
    default: () => FONT_SIZE_DEFAULT,
    sameSite: 'lax',
    maxAge: 60 * 60 * 24 * 365, // persist ~1 year, survives across sessions
  })

  // Read on the server: useCookie pulls the request cookie during SSR, so the
  // <html> font-size in app.vue's useHead is already correct in the server-
  // rendered HTML (no client flash). useState shares that value app-wide.
  const fontSize = useState('html-font-size', () => clamp(Number(cookie.value)))

  // Save on change. Runs client-side when the slider moves; keeps cookie + state
  // in sync so the choice is restored on the next request.
  watch(fontSize, (value) => {
    cookie.value = value
  })

  const setFontSize = (value: number) => {
    fontSize.value = clamp(value)
  }

  return { fontSize, setFontSize, min: FONT_SIZE_MIN, max: FONT_SIZE_MAX }
}
